// src/components/layout/Navigation.tsx

import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../hooks/useAuth';
import { useCartStore } from '../../stores/cartStore';

export const Navigation: React.FC = () => {
  const { t } = useLanguage();
  const { isAdmin } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const itemCount = useCartStore((state) => state.itemCount);
  const [animateCart, setAnimateCart] = useState(false);

  // Trigger bounce animation on cart count changes
  useEffect(() => {
    if (itemCount > 0) {
      setAnimateCart(true);
      const timer = setTimeout(() => setAnimateCart(false), 550);
      return () => clearTimeout(timer);
    }
  }, [itemCount]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Dinamička lista linkova koja uključuje Admin Dashboard samo za admine
  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/products', label: t('nav.products') },
    { to: '/stores', label: t('nav.stores') },
    { to: '/chains', label: t('nav.chains') },
    { to: '/archives', label: t('nav.archives') },
    { to: '/favorites', label: t('nav.favorites') },
    { to: '/cart', label: t('nav.cart') },
    ...(isAdmin ? [{ to: '/admin', label: t('nav.admin') }] : []),
  ];

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center h-full text-gray-700 hover:text-primary-600 font-medium transition-colors border-b-2 ${
      isActive ? 'text-primary-600 border-primary-600' : 'border-transparent'
    }`;

  return (
    <nav className="bg-white border-b">
      <div className="container mx-auto px-4">
        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-8 h-12">
          {navLinks.map((link) => (
            link.to === '/cart' ? (
              <NavLink key={link.to} to={link.to} className={navLinkClass}>
                <div className="flex items-center gap-1.5 relative">
                  <ShoppingCart className="h-4 w-4 shrink-0" />
                  <span>{link.label}</span>
                  {itemCount > 0 && (
                    <span 
                      className={`flex items-center justify-center bg-primary-600 text-white text-[10px] font-bold h-5 min-w-[20px] px-1.5 rounded-full border-2 border-white shadow-sm transition-transform ${
                        animateCart ? 'animate-badge-pop' : ''
                      }`}
                    >
                      {itemCount}
                    </span>
                  )}
                </div>
              </NavLink>
            ) : (
              <NavLink key={link.to} to={link.to as string} className={navLinkClass}>
                {link.label}
              </NavLink>
            )
          ))}
        </div>

        {/* Mobile Navigation Toggle */}
        <div className="md:hidden flex items-center justify-between h-12">
          <button
            onClick={toggleMobileMenu}
            className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-primary-600 hover:bg-gray-100 focus:outline-none"
            aria-expanded={isMobileMenuOpen}
          >
            <span className="sr-only">{t('nav.openMenu')}</span>
            {!isMobileMenuOpen ? (
              <svg className="block h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            ) : (
              <svg className="block h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile Menu Content */}
        <div 
          className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            isMobileMenuOpen ? 'max-h-96 opacity-100 border-t shadow-lg' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="px-2 pt-2 pb-3 space-y-1 bg-white">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to as string}
                className={({ isActive }) =>
                  `block px-3 py-2 rounded-md text-base font-medium transition-colors ${
                    isActive ? 'text-primary-600 bg-primary-50' : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                  }`
                }
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.to === '/cart' ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ShoppingCart className="h-5 w-5" />
                      <span>{link.label}</span>
                    </div>
                    {itemCount > 0 && (
                      <span 
                        className={`flex items-center justify-center bg-primary-600 text-white text-[11px] font-bold h-5 min-w-[22px] px-1 rounded-full border-2 border-white shadow-sm ${
                          animateCart ? 'animate-badge-pop' : ''
                        }`}
                      >
                        {itemCount}
                      </span>
                    )}
                  </div>
                ) : (
                  <span>{link.label}</span>
                )}
              </NavLink>
            ))}
          </div>
        </div>
      </div>
    </nav>
  );
};